import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import Textfield from '@atlaskit/textfield';
import { B400, N0, N90, N900 } from '@atlaskit/theme/colors';
import Tooltip from '@atlaskit/tooltip';
import styled from '@emotion/styled';
import React, {
  ChangeEventHandler,
  MouseEventHandler,
  useEffect,
  useState
} from 'react';
import { FormattedMessage } from 'react-intl';
import { usePriceFinderStore } from '../store';
import {
  deviceMediaQueries,
  TRACKING_EVENT_NAMES,
  UNIT_TRACKING_TERMS
} from '../utils/constants';

const DEFAULT_NUM_DIGITS_ALLOWED = 5;

export type UnitCountProps = {
  readonly maxUnits?: number;
  readonly maxNumberOfDigits?: number;
  readonly unitTrackingTerm?: UNIT_TRACKING_TERMS;
};

export const UnitCount = ({
  maxUnits,
  maxNumberOfDigits = DEFAULT_NUM_DIGITS_ALLOWED,
  unitTrackingTerm = UNIT_TRACKING_TERMS.agents
}: UnitCountProps) => {
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [state, { updateUnitCount }] = usePriceFinderStore();
  const [currentUnits, setCurrentUnits] = useState(state.unitCount.toString());
  const [storedUnits, setStoredUnits] = useState(currentUnits);

  useEffect(() => {
    setCurrentUnits(state.unitCount.toString());
  }, [state.unitCount]);

  const onChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    let value = target.value;
    let parsedValue = parseInt(value, 10);
    if (Number.isNaN(parsedValue)) {
      setCurrentUnits('');
    } else {
      if (parsedValue < 1) {
        setCurrentUnits('');
      } else {
        if (value.length > maxNumberOfDigits) {
          value = value.toString().slice(0, maxNumberOfDigits);
          parsedValue = parseInt(value, 10);
        }
        if (value.length === 1) {
          setStoredUnits(value.toString());
        }

        let newUnits = Math.max(parsedValue, 0);

        if (maxUnits) {
          newUnits = Math.min(newUnits, maxUnits);
        }

        setCurrentUnits(newUnits.toString());
        void updateUnitCount(newUnits);
      }
    }
  };

  const onBlur = ({ currentTarget }: React.FocusEvent<HTMLInputElement>) => {
    if (currentTarget.value.length === 0) {
      setCurrentUnits(storedUnits);
    } else {
      setStoredUnits(currentUnits.toString());
    }
  };

  const onClick: MouseEventHandler<HTMLInputElement> = ({ currentTarget }) => {
    currentTarget.select();
  };

  const onMouseOver = () => {
    if (createAnalyticsEvent) {
      const analyticsEvent = createAnalyticsEvent({
        name: TRACKING_EVENT_NAMES.unitDescriptionTooltipTriggered,
        label: unitTrackingTerm
      });
      analyticsEvent.fire();
    }
  };

  return (
    <>
      <HowManyUnitsMessageContainer>
        <FormattedMessage
          id="howManyUsers"
          defaultMessage="How many users are on your team?"
          description="Label for how many users input."
        />
      </HowManyUnitsMessageContainer>

      <UnitCountInputContainer>
        <Textfield
          onChange={onChange}
          value={currentUnits}
          selected={true}
          onBlur={onBlur}
          onClick={onClick}
          width="138"
        />

        <TooltipBase>
          <Tooltip
            content={
              <TooltipMessageContainer>
                <FormattedMessage
                  id="userTooltipMessage"
                  defaultMessage="A Jira Software user is any user who can log in to Jira Software, and who exists in User Management."
                  description="What is a (user or agent) tooltip label."
                />
              </TooltipMessageContainer>
            }
            position={'top'}
          >
            <TooltipContainer onMouseOver={() => onMouseOver()} tabIndex={0}>
              <QuestionCircleIcon
                size="medium"
                label="?"
                primaryColor={`${N90}`}
              />
            </TooltipContainer>
          </Tooltip>
        </TooltipBase>
      </UnitCountInputContainer>
    </>
  );
};

const HowManyUnitsMessageContainer = styled.h4`
  color: ${N900};
  font-size: 20px;
  grid-column: 1 / 4;
  letter-spacing: 0.3px;
  line-height: 24px;
  margin: 0;
  padding-right: 12px;
  padding-top: 10px;

  ${deviceMediaQueries.desktop} {
    font-size: 20px;
    grid-column: 2;
    line-height: 24px;
  }
`;

const UnitCountInputContainer = styled.div`
  display: flex;

  input {
    background-color: ${N0};
    box-sizing: border-box;
    color: ${B400};
    font-size: 24px;
    height: 40px !important;
    left: 0px;
    line-height: 32px;
    top: 0px;
    width: 134px;
  }

  ${deviceMediaQueries.tablet} {
    justify-content: center;
    padding-top: 16px;
    width: 100%;
  }
`;

const TooltipBase = styled.div`
  grid-column: 4;
  padding-left: 5px;
  padding-top: 10px;

  > div {
    width: 25px;
  }
`;

const TooltipMessageContainer = styled.div`
  font-family: Charlie Text;
`;

const TooltipContainer = styled.div`
  > span {
    display: block;
    line-height: 0;
  }
`;
